"use client";

import { useEffect } from "react";

import { fetchJson } from "@/lib/api";
import { showToast } from "@/lib/toast";
import { currentPushSubscription, pushSupport, subscribeToPush } from "@/lib/push/client";

const SNOOZE_KEY = "hub:notification-prompt-snoozed-until";
const SNOOZE_DAYS = 7;
// The shell remounts between navigations; keep the prompt to once per session.
let promptedThisSession = false;

/**
 * Asks for notification permission once per browser (when it is still
 * undecided). The request is shown as a system message at the top right, and
 * already granted/denied browsers are left alone. Later changes live in the
 * notification settings screen.
 */
export function NotificationPermissionPrompt({ workspaceSlug }: { workspaceSlug?: string }) {
  useEffect(() => {
    if (promptedThisSession) return;
    const support = pushSupport();
    if (!support.supported || typeof Notification === "undefined") return;
    if (Notification.permission !== "default") return;
    try {
      const snoozedUntil = Number(window.localStorage.getItem(SNOOZE_KEY) ?? 0);
      if (Number.isFinite(snoozedUntil) && snoozedUntil > Date.now()) return;
    } catch {
      // Ignore storage failures and keep the prompt available.
    }

    async function allow() {
      try {
        const config = await fetchJson<{ configured: boolean; public_key: string }>("/api/push/config", { cache: "no-store" }, { silent: true });
        if (!config.configured || !config.public_key) {
          if (typeof Notification !== "undefined" && Notification.permission === "default") await Notification.requestPermission();
          showToast("このサーバーではWeb Pushが未設定です（通知設定を確認してください）", "error");
          return;
        }
        const existing = await currentPushSubscription();
        const subscription = existing ?? await subscribeToPush(config.public_key);
        if (!existing) {
          const json = subscription.toJSON() as { endpoint?: string; keys?: Record<string, string> };
          await fetchJson("/api/push/subscribe", { method: "POST", body: JSON.stringify({ endpoint: json.endpoint, keys: json.keys ?? {} }) });
        }
        if (workspaceSlug) {
          await fetchJson(`/api/workspaces/${encodeURIComponent(workspaceSlug)}/notification-preferences`, { method: "PATCH", body: JSON.stringify({ channels: { browser_push: true } }) }).catch(() => undefined);
        }
        showToast("この端末で通知を受け取れるようにしました");
      } catch (error) {
        showToast(error instanceof Error ? error.message : "通知の許可に失敗しました", "error");
      }
    }

    function snooze() {
      try { window.localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_DAYS * 24 * 60 * 60 * 1000)); } catch { /* ignore */ }
    }

    promptedThisSession = true;
    showToast(
      "通知の許可が未設定です。許可するとこの端末（Windows/iOS）で、アプリを閉じていても通知が届きます。",
      "success",
      [
        { label: "あとで", onClick: snooze },
        { label: "許可する", onClick: () => { void allow(); }, primary: true },
      ],
    );
  }, [workspaceSlug]);

  return null;
}
